import React, { useState, useEffect } from "react";
import "@/App.css";
import BootScreen from "@/os/BootScreen";
import Desktop from "@/os/Desktop";
import LoginScreen from "@/os/LoginScreen";
import HexaExperience from "@/os/experience/HexaExperience";
import MarketingLanding from "@/os/MarketingLanding";
import BlogPage from "@/os/BlogPage";
import PrivacyPolicy from "@/os/PrivacyPolicy";
import TermsConditions from "@/os/TermsConditions";
import FAQPage from "@/os/FAQPage";
import { AuthProvider, useAuth } from "@/os/AuthContext";
import { ThemeProvider } from "@/os/ThemeContext";
import DemoBanner from "@/os/DemoBanner";
import DemoPromptModal from "@/os/DemoPromptModal";

const API = process.env.REACT_APP_BACKEND_URL;

// Inject GA4 / GTM / Pixel scripts once
function injectTrackingScripts(codes) {
  // GA4
  if (codes.ga4_id && !document.getElementById("hexa-ga4")) {
    const s1 = document.createElement("script");
    s1.id = "hexa-ga4";
    s1.async = true;
    s1.src = `https://www.googletagmanager.com/gtag/js?id=${codes.ga4_id}`;
    document.head.appendChild(s1);
    const s2 = document.createElement("script");
    s2.id = "hexa-ga4-init";
    s2.innerHTML = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${codes.ga4_id}');`;
    document.head.appendChild(s2);
  }
  // GTM
  if (codes.gtm_id && !document.getElementById("hexa-gtm")) {
    const s = document.createElement("script");
    s.id = "hexa-gtm";
    s.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${codes.gtm_id}');`;
    document.head.appendChild(s);
  }
  // Facebook Pixel
  if (codes.pixel_id && !document.getElementById("hexa-pixel")) {
    const s = document.createElement("script");
    s.id = "hexa-pixel";
    s.innerHTML = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${codes.pixel_id}');fbq('track','PageView');`;
    document.head.appendChild(s);
  }
}

// Track pageview to HEXA analytics
function trackPageview(user) {
  try {
    const sid = sessionStorage.getItem("hexa_session_id") || (() => {
      const id = Math.random().toString(36).slice(2);
      sessionStorage.setItem("hexa_session_id", id);
      return id;
    })();
    fetch(`${API}/api/analytics/pageview`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        page: window.location.pathname,
        referrer: document.referrer,
        session_id: sid,
        user_address: user?.address || "",
        source: user ? "app" : "website",
      }),
    }).catch(() => {});
  } catch (_) {}
}

if ("serviceWorker" in navigator && process.env.NODE_ENV !== "development") {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  });
}

// Simple pathname routing for the static-content pages (privacy / terms / faq).
// Everything else falls back to the SPA marketing landing / OS shell.
function useCurrentRoute() {
  const [path, setPath] = useState(() => window.location.pathname);
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const navigate = (p) => {
    window.history.pushState({}, "", p);
    setPath(p);
  };
  return [path, navigate];
}

function Shell() {
  const [booted, setBooted] = useState(false);
  const { user, checking, demo, exitDemo } = useAuth();
  const [authView, setAuthView] = useState(null);
  const [demoPrompt, setDemoPrompt] = useState(null);
  const [path, navigate] = useCurrentRoute();

  // Auto-capture referral code from ?ref= into localStorage so signup form pre-fills it.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) {
      localStorage.setItem("hexa_ref_code", ref);
      const url = new URL(window.location.href);
      url.searchParams.delete("ref");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  // Load tracking codes on startup and inject scripts
  useEffect(() => {
    fetch(`${API}/api/public/tracking-codes`)
      .then(r => r.ok ? r.json() : {})
      .then(codes => { if (Object.keys(codes).length > 0) injectTrackingScripts(codes); })
      .catch(() => {});
    // Track this pageview
    trackPageview(null);
  }, []);

  useEffect(() => {
    const handler = (e) => setDemoPrompt(e.detail || {});
    window.addEventListener("hexa:demo-prompt", handler);
    return () => window.removeEventListener("hexa:demo-prompt", handler);
  }, []);

  // Sync path-based auth view with URL so /login and /signup are shareable
  useEffect(() => {
    if (path === "/login" && !user) setAuthView("login");
    else if (path === "/signup" && !user) setAuthView("signup");
    else if (path !== "/login" && path !== "/signup" && authView) setAuthView(null);
  }, [path, user, authView]);

  // Static content pages — accessible without login, ignore boot animation
  if (path === "/privacy") return <PrivacyPolicy onBack={() => navigate("/")} />;
  if (path === "/terms") return <TermsConditions onBack={() => navigate("/")} />;
  if (path === "/faq") return <FAQPage onBack={() => navigate("/")} />;
  // /blog lists posts; /blog/<slug> renders one, so posts have shareable,
  // crawlable URLs instead of living only in component state.
  if (path === "/blog" || path.startsWith("/blog/"))
    return (
      <BlogPage
        slug={path.startsWith("/blog/") ? decodeURIComponent(path.slice(6)) : null}
        onBack={() => navigate("/")}
        onNavigate={navigate}
      />
    );

  if (!booted) return <BootScreen onDone={() => setBooted(true)} />;
  if (checking) return <BootScreen onDone={() => {}} />;
  if (!user) {
    if (authView === null && path !== "/login" && path !== "/signup") {
      // Legacy 3D immersive experience is still routable at /immersive for reference.
      if (path === "/immersive") {
        return (
          <HexaExperience
            onLogin={() => navigate("/login")}
            onRegister={() => navigate("/signup")}
          />
        );
      }
      return (
        <MarketingLanding
          onLogin={() => navigate("/login")}
          onRegister={() => navigate("/signup")}
          onNavigate={navigate}
        />
      );
    }
    return <LoginScreen initialMode={authView || (path === "/signup" ? "signup" : "login")} onBack={() => { setAuthView(null); navigate("/"); }} />;
  }
  return (
    <>
      {demo && <DemoBanner onSignup={() => { exitDemo(); setAuthView("signup"); }} onExit={() => { exitDemo(); }} />}
      <Desktop />
      {demoPrompt && (
        <DemoPromptModal
          detail={demoPrompt}
          onClose={() => setDemoPrompt(null)}
          onSignup={() => { setDemoPrompt(null); exitDemo(); setAuthView("signup"); }}
          onLogin={() => { setDemoPrompt(null); exitDemo(); setAuthView("login"); }}
        />
      )}
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <Shell />
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}
